import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { medicinesApi } from "../../../shared/api/medicines";
import type { MedicineSummaryResponse } from "../../../shared/types/medicine";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from "@/components/ui/table";

interface Props {
  open: boolean;
  onClose: () => void;
  onPick: (medicine: MedicineSummaryResponse) => void;
}

/** Type-ahead modal that picks a medicine from `/api/v1/medicines`. */
export function MedicinePicker({ open, onClose, onPick }: Props) {
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    if (!open) {
      setQ("");
      setDebounced("");
    }
  }, [open]);

  const results = useQuery({
    queryKey: ["medicines", "picker", debounced],
    queryFn: () =>
      medicinesApi.list(
        { q: debounced || null, visibleOnly: true },
        { page: 0, size: 20, sort: "name" }
      ),
    enabled: open,
  });

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Chọn thuốc</DialogTitle>
        </DialogHeader>

        <div className="relative">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            type="search"
            className="pl-9"
            placeholder="Tìm theo mã, tên, hoạt chất, hãng dược…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            autoFocus
          />
        </div>

        <div>
          {results.isLoading && (
            <p className="text-sm text-muted-foreground">Đang tải…</p>
          )}
          {results.data?.content.length === 0 && (
            <p className="text-sm text-muted-foreground">
              Không có thuốc nào khớp. Hãy tạo thuốc trước ở "Cài đặt → Thuốc".
            </p>
          )}
          {(results.data?.content.length ?? 0) > 0 && (
            <div className="max-h-[420px] overflow-y-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Mã</TableHead>
                    <TableHead>Tên</TableHead>
                    <TableHead>Hàm lượng</TableHead>
                    <TableHead>Đơn vị</TableHead>
                    <TableHead>Hãng</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {results.data!.content.map((m) => (
                    <TableRow key={m.id}>
                      <TableCell className="font-mono">{m.internalCode}</TableCell>
                      <TableCell>
                        <strong>{m.name}</strong>
                      </TableCell>
                      <TableCell>{m.strength ?? "—"}</TableCell>
                      <TableCell>{m.unit}</TableCell>
                      <TableCell>{m.manufacturer ?? "—"}</TableCell>
                      <TableCell>
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => {
                            onPick(m);
                            onClose();
                          }}
                        >
                          Chọn
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
